<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import SearchBox from '@/components/SearchBox.vue'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import UiRow from '@/components/UiRow.vue'
import { useSearchQuery } from '@/composables/useSearchQuery'
import { SHARING_SEARCH_MIN, searchBy } from '@/settings/grantGroups'

// A list of groups that each open a page (calendars or grantees) with the number of entries as `2 items`; a search from the sixth row on.
const props = defineProps<{ items: { key: string; to: string; title: string; count: number }[]; emptyText: string }>()
const { t } = useI18n()
const query = useSearchQuery()
const searchable = computed(() => props.items.length >= SHARING_SEARCH_MIN)
const shown = computed(() => (searchable.value ? searchBy(props.items, query.value, (i) => [i.title]) : props.items))
</script>

<template>
  <div class="count-list">
    <UiGroup v-if="searchable">
      <SearchBox v-model="query" :placeholder="t('list.search')" :clear-label="t('list.clearSearch')" />
      <template #footer>
        <p v-if="query && !shown.length">{{ t('settings.blocked.noMatches') }}</p>
      </template>
    </UiGroup>
    <UiGroup>
      <template #footer>
        <p v-if="!items.length">{{ emptyText }}</p>
      </template>
      <UiList v-if="shown.length">
        <UiRow v-for="item in shown" :key="item.key" :to="item.to" class="entry" data-testid="count-row">
          <span class="title">{{ item.title }}</span>
          <span class="ui-hint count">{{ t('settings.blocked.itemsCount', { n: item.count }, item.count) }}</span>
          <span class="go" aria-hidden="true">›</span>
        </UiRow>
      </UiList>
    </UiGroup>
  </div>
</template>

<style scoped>
.entry {
  color: var(--tg-text-color);
  text-decoration: none;
}

.title {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

.count {
  flex: none;
}

.go {
  flex: none;
  color: var(--tg-hint-color);
  font-size: 1.2rem;
}
</style>
